<template>
  <span class="d-book-vote-button">
    <span
      ref="button"
      class="d-book-vote-button-container button text-no-transform"
      :class="voteStateClass"
      :disabled="voteData.isProcessing"
      title="Upvote"
      @click.stop.prevent="upvote()"
    >
      <span class="vote-content">
        <DuiIconTriangle
          class="icon-sm"
          :class="vm.upvoteIconClass"
        />

        <span
          v-if="displayLabel"
          class="vote-label"
        >
          <span v-if="voteData.isUpvoted">Upvoted</span>
          <span v-if="!voteData.isUpvoted">Upvote</span>
        </span>

        <span>{{ voteData.numVotes }}</span>
      </span>

      <MountingPortal
        v-if="voteData.isUpvoted && vm.authorInfo && vm.shouldShowTip"
        mount-to="#portal-container"
        append
      >
        <DFollowTip
          :author-info="vm.authorInfo"
          :button="$refs.button"
          @close-tip="closeTip"
        />
      </MountingPortal>
    </span>
  </span>
</template>

<script>
import Vue from 'vue';

import DFollowTip from '../d-follow-tip/d-follow-tip.vue';
import bookVotesService from '@d-services/book-votes-service/book-votes-service';
import { getErrorMessage } from '@d-shared/utils/errors';
import storeKey from '@dui/mixins/store-key';

const CLASSES = {
  defaultButton: 'button-white-bordered',
  whiteIcon: 'icon-white',
};

export default {
  components: {
    DFollowTip,
  },
  mixins: [
    storeKey,
  ],
  props: {
    bookSlug: {
      type: String,
      required: true,
    },
    buttonClass: {
      type: String,
      default: CLASSES.defaultButton,
    },
    displayLabel: {
      type: Boolean,
      default: true,
    },
    isUpvoted: {
      type: Boolean,
      default: false,
    },
    numVotes: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    vm: {
      upvoteIconClass: null,
      voteData: {
        isProcessing: false,
        isUpvoted: false,
        numVotes: null,
      },
      shouldShowTip: false,
      authorInfo: null,
    },
  }),
  computed: {
    storeData() {
      return this.loadFromStore();
    },
    voteData() {
      return this.storeData || this.vm.voteData;
    },
    voteStateClass() {
      return this.voteData.isUpvoted ? 'button-yellow' : this.buttonClass;
    },
  },
  watch: {
    'voteData.isUpvoted': function() {
      this.setIconClass();
    },
  },
  mounted() {
    this.setInitialData();
  },
  methods: {
    async addUpvote() {
      this.$set(this.voteData, 'isProcessing', true);

      try {
        const authorInfo = await bookVotesService.addUpvote(this.bookSlug);
        this.$set(this.vm, 'authorInfo', authorInfo);
        this.$set(this.voteData, 'isUpvoted', true);
        if (this.vm.authorInfo) this.$set(this.vm, 'shouldShowTip', true);
        this.increaseNumVotes();
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this.voteData, 'isProcessing', false);
    },

    decreaseNumVotes() {
      this.$set(this.voteData, 'numVotes', this.voteData.numVotes - 1);
    },

    increaseNumVotes() {
      this.$set(this.voteData, 'numVotes', this.voteData.numVotes + 1);
    },

    async removeUpvote() {
      this.$set(this.voteData, 'isProcessing', true);

      try {
        await bookVotesService.removeUpvote(this.bookSlug);
        this.$set(this.voteData, 'isUpvoted', false);
        this.decreaseNumVotes();
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this.voteData, 'isProcessing', false);
    },

    setInitialData() {
      this.$set(this.voteData, 'isUpvoted', this.isUpvoted);
      this.$set(this.voteData, 'numVotes', this.numVotes);
      this.setIconClass();

      if (this.storeSaveKey && !this.storeData) {
        this.$store.dispatch(this.storeSaveKey, this.vm.voteData);
      }
    },

    setIconClass() {
      if (this.voteData.isUpvoted ||
        this.buttonClass !== CLASSES.defaultButton) {
        this.$set(this.vm, 'upvoteIconClass', CLASSES.whiteIcon);
        return;
      }

      this.$set(this.vm, 'upvoteIconClass', null);
    },

    upvote() {
      if (this.voteData.isUpvoted) {
        this.removeUpvote();
        return;
      }

      this.addUpvote();
      return;
    },

    closeTip() {
      this.$set(this.vm, 'shouldShowTip', false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";
.d-book-vote-button {
  position: relative;

  // XS
  &.xs .d-book-vote-button-container {
    border-radius: $border-radius-xl;
    height: $_3em;
    font-size: $font-size-sm;
    padding: $_0-5em $_1em;

    .dui-icon {
      height: $_1em;
      width: $_1em;
    }
  }
  // END XS

  // SM
  &.sm .d-book-vote-button-container {
    font-size: $font-size-sm;
    padding: $_1-5em;
  }
  // END SM

  // LG
  &.lg .d-book-vote-button-container {
    font-size: $font-size-lg;
    padding: $_2em;

    .dui-icon {
      height: 14px;
      width: 14px;
    }
  }
  // END LG

  .d-book-vote-button-container {
    position: relative;
    display: inline-flex;
    justify-content: center;
    line-height: $font-size-base;

    .vote-content {
      align-items: center;
      display: inline-flex;

      > * {
        margin-left: $_1em;

        &:first-child {
          margin-left: 0;
        }
      }
    }

    .dui-icon {
      margin: 0 !important;
    }
  }
}
</style>
